
import { Duration } from "luxon";

import {inflate} from 'pako';
import { Partitioner } from "./Partitioner";
import { isTornadoFeatureCollection, Tornado, TornadoMin, TornadoGeoJSONMin, TornadoProperties, TornadoPropertiesNoCollection, TornadoPropertiesMin, 
        TornadoNoFeatureCollection, 
        TornadoGeoJSON} from "./tornado";

import { Map, CircleLayerSpecification, FillLayerSpecification, LineLayerSpecification, PainterOptions } from "maplibre-gl";

import {PlateCarreeGrid, RawScalarField, Grid, RawProfileField } from 'autumnplot-gl';

const randomChoice = <T>(ary: T[]): T => {
    return ary[Math.floor(Math.random() * ary.length)];
}

function* zip(...args: any[]) {
	const iterators = args.map(x => x[Symbol.iterator]());
	while (true) {
		const current = iterators.map(x => x.next());
		if (current.some(x => x.done)) {
			break;
		}
		yield current.map(x => x.value);
	}
}

function pairwise<T>(ary: T[]) {
	let ary1 = ary.slice();
    let ary2 = ary.slice();

    ary1.pop()
    ary2.shift();
  
    return zip(ary1, ary2);
}

function downloadGzipCompressed(rem_fname: string, pako_opts: pako.InflateFunctionOptions & { to: 'string' }): Promise<string>;
function downloadGzipCompressed(rem_fname: string, pako_opts?: pako.InflateFunctionOptions) : Promise<Uint8Array>;
function downloadGzipCompressed(rem_fname: string, pako_opts?: pako.InflateFunctionOptions) : Promise<Uint8Array | string> {
    return fetch(rem_fname)
        .then(resp => resp.blob())
        .then(blob => blob.arrayBuffer())
        .then(bufr => new Uint8Array(bufr))
        .then(arry => inflate(arry, pako_opts))
}

function downloadHodographs(rem_fname: string) {
    return downloadGzipCompressed(rem_fname)
        .then(data_packed => {
            const n_profiles = new Uint16Array(data_packed.buffer, 0, 1)[0];
            const profile_lengths = new Uint8Array(data_packed.buffer, 2, n_profiles);
            const n_pad = 4 - (n_profiles + 2) % 4;

            let i_start = n_profiles + 2 + n_pad;
            const profiles = [...profile_lengths].map(prof_len => {
                const [lat, lon] = new Float32Array(data_packed.buffer, i_start, 2);
                const [jlat, ilon] = new Uint16Array(data_packed.buffer, i_start + 8, 2);
                const [storm_u, storm_v] = new Float32Array(data_packed.buffer, i_start + 12, 2);

                const u = new Float32Array(data_packed.buffer, i_start + 20, prof_len);
                const v = new Float32Array(data_packed.buffer, i_start + 20 + 4 * prof_len, prof_len);
                const z = new Float32Array(data_packed.buffer, i_start + 20 + 2 * 4 * prof_len, prof_len);

                i_start += (20 + 3 * 4 * prof_len);

                return {'lat': lat, 'lon': lon, 'jlat': jlat, 'ilon': ilon, 'smu': storm_u, 'smv': storm_v, 
                        'u': u, 'v': v, 'z': z};
            });

            // XXX: This assumes a PlateCarree grid. That will break when RAP data are introduced
            const min_lat = profiles.map(prof => prof['lat']).reduce((a, b) => Math.min(a, b));
            const max_lat = profiles.map(prof => prof['lat']).reduce((a, b) => Math.max(a, b));
            const min_lon = profiles.map(prof => prof['lon']).reduce((a, b) => Math.min(a, b));
            const max_lon = profiles.map(prof => prof['lon']).reduce((a, b) => Math.max(a, b));

            const ni_start = profiles.map(prof => prof['ilon']).reduce((a, b) => Math.min(a, b));
            const ni = profiles.map(prof => prof['ilon']).reduce((a, b) => Math.max(a, b)) + 1;

            // I was a dum-dum and put 0, 0 for the profile indices at the *northwest* corner of the domain.
            const nj_ = profiles.map(prof => prof['jlat']).reduce((a, b) => Math.max(a, b)) + 1;
            profiles.forEach(prof => {
                prof['jlat'] = nj_ - prof['jlat'] - 1;
            });

            const nj = profiles.map(prof => prof['jlat']).reduce((a, b) => Math.max(a, b)) + 1;
            const nj_start = profiles.map(prof => prof['jlat']).reduce((a, b) => Math.min(a, b));
            const dlon = (max_lon - min_lon) / (ni - ni_start - 1);
            const dlat = (max_lat - min_lat) / (nj - nj_start - 1);

            const grid = new PlateCarreeGrid(ni, nj, min_lon - ni_start * dlon, min_lat - nj_start * dlat, max_lon, max_lat);
            const profile_grid = new RawProfileField(grid, profiles);

            return profile_grid;
        });
}

async function downloadGrid(rem_fname: string) {
    function gridFactory(grid_type_num: number, buffer: Uint8Array): {'grid': Grid, 'nbytesread': number} {
        switch (grid_type_num) {
            case 0:
                const dims = new Uint16Array(buffer.buffer, offset, 2);
                const [ni, nj] = [...dims];
        
                const grid_def = new Float32Array(buffer.buffer, offset + 4, 4);
                const [ll_lon, ll_lat, ur_lon, ur_lat] = [...grid_def];
        
                return {
                    'grid': new PlateCarreeGrid(ni, nj, ll_lon, ll_lat, ur_lon, ur_lat),
                    'nbytesread': 20
                }
            case 1:
                throw "Lambert Conformal grids not implemented yet";
            default:
                throw `Unknown grid type: ${grid_type_num}`;
        }
    }

    const data_packed = await downloadGzipCompressed(rem_fname);

    let grid_type_num = new Uint16Array(data_packed.buffer, 0, 1)[0];

    let offset = 4;
    const {grid, nbytesread} = gridFactory(grid_type_num, data_packed);

    offset += nbytesread;

    const data = new Float32Array(data_packed.buffer, offset, grid.ni * grid.nj);
    return new RawScalarField(grid, data);
}

function timeIsKnown(tor_props: TornadoProperties | TornadoPropertiesNoCollection) {
    let time_accuracy: Duration;
    if (tor_props['time-accuracy'] !== undefined) {
        let acc_str = tor_props['time-accuracy'];
        if (acc_str.startsWith('GT')) {
            acc_str = acc_str.slice(2)
        }

        let lead_str = 'PT';
        if (acc_str.includes('D')) {
            lead_str = 'P';
        }

        time_accuracy = Duration.fromISO(lead_str + acc_str);
    }
    else if (tor_props['utc_check'] !== undefined && tor_props['utc_check'] == false) {
        time_accuracy = Duration.fromISO('P1D');
    }
    else {
        time_accuracy = Duration.fromISO('PT1H');
    }

    return time_accuracy <= Duration.fromISO('PT6H');
}

const hex2rgba = (hexstr: string, out_type?: string) : [number, number, number, number?] => {
    out_type = out_type === undefined ? 'float' : out_type;

    const match = hexstr.match(/#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?/i);
    if (match === null) {
        throw `Got '${hexstr}' in hex2rgba, which does not look like a hex color`;
    }

    let rgba = match.slice(1).filter(c => c !== undefined).map(c => parseInt(c, 16));

    if (out_type == 'float') {
        rgba = rgba.map(c => c / 255);
    }

    return rgba[3] === undefined ? [rgba[0], rgba[1], rgba[2]] : [rgba[0], rgba[1], rgba[2], rgba[3]];
}

const rgba2hex = (rgba: [number, number, number, number?], in_type?: string) : string => {
    in_type = in_type === undefined ? 'float' : in_type;

    let rgba_ = rgba as number[];
    if (in_type == 'float') {
        rgba_ = rgba_.filter((c: number | undefined) : c is number => c !== undefined).map(c => Math.round(c * 255));
    }

    return '#' + rgba_.map(c => c.toString(16).padStart(2, '0').toUpperCase()).join('');
}

const colorBlend = (color1: string, color2: string): string => {
    const float_color1 = hex2rgba(color1);
    const float_color2 = hex2rgba(color2);

    const alpha1 = float_color1[3] === undefined ? 1. : float_color1[3];
    const alpha2 = float_color2[3] === undefined ? 1. : float_color2[3];
    const alpha_blend = alpha1 + alpha2 * (1 - alpha1);

    const blend_channel = (c1: number, c2: number) => (c1 * alpha1 + c2 * alpha2 * (1 - alpha1)) / alpha_blend;

    return rgba2hex([
        blend_channel(float_color1[0], float_color2[0]),
        blend_channel(float_color1[1], float_color2[1]),
        blend_channel(float_color1[2], float_color2[2]),
        alpha_blend
    ]);
}

const addLayerToMap = (map: Map, layer_id: string, layer_json: GeoJSON.FeatureCollection, partitioner: Partitioner) : void => {
    // Add layer to the map
    const split_idx = layer_id.lastIndexOf('-')
    const pk = layer_id.substring(0, split_idx);
    const layer_type = layer_id.substring(split_idx + 1);

    const color = partitioner.colors[partitioner.part_keys.indexOf(pk)];

    var type: CircleLayerSpecification['type'] | FillLayerSpecification['type'] | LineLayerSpecification['type'], layout: any, paint: any; // Fix these 'any's

    if (layer_type == 'points') {
        type = 'circle';
        layout = {};
        paint = {
            'circle-color': color,
            'circle-radius': 4,
        };
    }
    else if (layer_type == 'polys') {
        const poly_opacity = 0.5;
        const poly_opacity_hex = Math.round(poly_opacity * 255).toString(16).padStart(2, '0');
        const poly_color = colorBlend(color + poly_opacity_hex, '#818888ff').slice(0, -2);

        type = 'fill'
        layout = {};
        paint = {
            'fill-color': poly_color,
            'fill-opacity': 1.0,
        };
    }
    else if (layer_id.includes('track-markers')) {
        type = 'circle';
        layout = {};
        paint = {
            'circle-color': '#ffffff',
            'circle-radius': 2,
            'circle-opacity': 0.6,
        };
    }
    else {
        type = 'line';
        layout = {'line-cap': 'round'};
        paint = {
            'line-color': color,
            'line-width': 6,
        };
    }

    map.addSource(layer_id, {
        type: 'geojson',
        data: layer_json,
        tolerance: 0.01,
    });

    map.addLayer({
        id: layer_id,
        source: layer_id,
        type: type,
        layout: layout,
        paint: paint
    } as (CircleLayerSpecification | FillLayerSpecification | LineLayerSpecification), 'road_area_pier');
}

const torsToLayers = (tors: Record<string, TornadoGeoJSONMin> | Record<string, TornadoGeoJSON> | null, partitioner: Partitioner)
        : Record<string, GeoJSON.FeatureCollection> => {
    const GEOM_TYPES: Record<string, string> = {'point': 'points', 'linestring': 'lines', 'polygon': 'polys'};

    var layers: Record<string, GeoJSON.FeatureCollection> = {};
    partitioner.part_keys.forEach(pk => {
        layers[pk + '-lines'] = {'type': 'FeatureCollection', 'features': []};
        layers[pk + '-points'] = {'type': 'FeatureCollection', 'features': []};
        layers[pk + '-polys'] = {'type': 'FeatureCollection', 'features': []};
        layers[pk + '-track-markers'] = {'type': 'FeatureCollection', 'features': []}
    });

    if (tors === null) {
        return layers;
    }

    var partition_keys = Object.keys(tors);

    partition_keys.forEach((pk, i) => {
        let feats = separateGeometryCollections(tors[pk]['features']) as GeoJSON.Feature<GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon>[];
        feats.forEach(feat => {
            if (feat['properties'] === null) return;

            const feat_pk = Object.entries(partitioner.partition([feat])).filter(([k, v]) => v.length > 0)[0][0];
            feat['properties']['popup'] = true;

            let layer_id = feat_pk + '-' + GEOM_TYPES[feat['geometry']['type'].toLowerCase()];
            if (feat['geometry']['type'] == 'Polygon') {
                feat['properties']['popup'] = false;
            }

            layers[layer_id]['features'].push(feat);

            if (feat['geometry']['type'] != 'Polygon') {
                // Add track marker
                
                let pt_feat: GeoJSON.Feature<GeoJSON.Point> = {
                    'type': 'Feature', 
                    'geometry': {'type': 'Point', 'coordinates': []}, 
                    'properties': {}
                };

                pt_feat['properties'] = {};

                Object.assign(pt_feat['properties'], feat['properties']);
                pt_feat['properties']['popup'] = false;

                if (feat['geometry']['type'] != 'Point') {
                    pt_feat['geometry']['coordinates'] = feat['geometry']['coordinates'][0];
                }
                else {
                    pt_feat['geometry']['coordinates'] = feat['geometry']['coordinates'];
                }

                layers[pk + '-track-markers']['features'].push(pt_feat);
            }
        });
    });

    return layers;
}

function separateGeometryCollections(tor_list: (Tornado | TornadoMin)[]): TornadoNoFeatureCollection[] {
    let tor_list_no_gc: TornadoNoFeatureCollection[] = [];

    tor_list.forEach(gj => {
        if (isTornadoFeatureCollection(gj)) {
            const coll_f_scales = gj['properties']['f-scale'];
            gj['geometry']['geometries'].forEach((geom, igeom) => {
                let new_props = Object.assign({}, gj['properties']) as Record<string, any>;
                new_props['f-scale'] = coll_f_scales[igeom];
                let new_tor: TornadoNoFeatureCollection = {
                    'type': 'Feature',
                    'geometry': geom,
                    'properties': new_props as TornadoPropertiesNoCollection
                };
                tor_list_no_gc.push(new_tor);
            })
        }
        else {
            tor_list_no_gc.push(gj as TornadoNoFeatureCollection);
        }
    })

    return tor_list_no_gc;
}

export {randomChoice, zip, pairwise, downloadGzipCompressed, downloadGrid, downloadHodographs, timeIsKnown, hex2rgba, torsToLayers, addLayerToMap, separateGeometryCollections, colorBlend};